

/* ==========================================================================
   SFF UTILITIES & PURE HELPERS (/database/utils.js)
   ========================================================================== */

import '/assets/shared/sff-contract.js?v=20260822-statebridge4';
import { debugRuntimeIssue } from './runtime.js?v=20260819-audit2';

export function cleanComponentName(str) {
  if (!str) return '';
  return String(str).replace(/\s*\([^)]*mm\)/gi, '').trim();
}

export function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function parseSpecNum(raw, fallback) {
  if (raw === null || raw === undefined || raw === '') return fallback;
  const str = String(raw).trim();
  const match = str.match(/([0-9]+(?:\.[0-9]+)?)/);
  if (!match) return fallback;
  const num = parseFloat(match[1]);
  return isNaN(num) ? fallback : num;
}

export function formatPrettyTitle(str) {
  if (!str) return '';
  return String(str).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

export function getDimensionToken(str, i) {
  if (!str || typeof str !== 'string') return '0';
  return str.toLowerCase().replace(/mm/g, '').trim().split(/\s*[x×X]\s*/)[i] || '0';
}

export function generateInlineNoteToggle(notesObj, key, idPrefix, caseIdx) {
  if (!notesObj) return "";
  let hasNote = key === 'gpu-combined' 
    ? !!(notesObj.gpu || notesObj.thick || notesObj.height) 
    : !!(notesObj[key] && notesObj[key].trim().length > 0);
  return !hasNote ? "" : `<span class="spec-micro-toggle" data-idx="${caseIdx}" data-key="${key}" data-target="${idPrefix}-${key}-box">NOTES ▼</span>`;
}

export function formatCaseBrandTitle(rawInput) {
  if (!rawInput) return '';

  let formatted = String(rawInput).replace(/[-_]+/g, ' ');
  formatted = formatted.replace(/\b\w/g, l => l.toUpperCase());

  // Restore missing decimal points for version numbers (e.g., V25 -> V2.5, V21 -> V2.1)
  formatted = formatted.replace(/\bV(\d)(\d)\b/gi, 'V$1.$2');

  const replacements = [
    [/\bssupd\b/gi, 'SSUPD'], [/\bncase\b/gi, 'NCASE'], [/\bnzxt\b/gi, 'NZXT'], [/\bevga\b/gi, 'EVGA'],
    [/\basus\b/gi, 'ASUS'], [/\bmsi\b/gi, 'MSI'], [/\bformd\b/gi, 'FormD'], [/\blouqe\b/gi, 'LOUQE'],
    [/\bdan\b/gi, 'DAN'], [/\bsff\b/gi, 'SFF'], [/\bitx\b/gi, 'ITX'], [/\batx\b/gi, 'ATX'],
    [/\bgpu\b/gi, 'GPU'], [/\bcpu\b/gi, 'CPU'], [/\baio\b/gi, 'AIO']
  ];

  replacements.forEach(([regex, replacement]) => {
    formatted = formatted.replace(regex, replacement);
  });

  return formatted;
}

export function generateBuildShareTitle(caseName, specs) {
  const cleanCase = formatCaseBrandTitle(caseName || 'SFF Case');
  if (!specs) return `${cleanCase} Specs & Compatibility - SFFLABS`;
  return `${cleanCase} Build - SFFLABS`;
}

export function slugifyUrlSegment(str) {
  return window.SFFContracts?.fallbackSlug?.(str) || 'build';
}

export function caseRouteSlug(caseOrValue, fallbackName = '') {
  return window.SFFContracts?.caseRouteSlug?.(caseOrValue, fallbackName) || slugifyUrlSegment(
    caseOrValue && typeof caseOrValue === 'object' ? (caseOrValue.name || fallbackName) : (caseOrValue || fallbackName)
  );
}

export const AIO_COMBOBOX_IDS = Object.freeze(['aioCoolerCombobox', 'aioSearchCombobox', 'aioCombobox']);

export function getComboboxInputValue(containerIds) {
  const ids = Array.isArray(containerIds) ? containerIds : [containerIds];
  for (const id of ids) {
    if (!id) continue;
    const value = document.querySelector(`#${id} .combobox-input`)?.value?.trim();
    if (value) return value;
  }
  return '';
}

export function syncComboboxInputValues(containerIds, value, sourceId = null) {
  const ids = Array.isArray(containerIds) ? containerIds : [containerIds];
  ids.forEach(id => {
    if (!id || id === sourceId) return;
    const input = document.querySelector(`#${id} .combobox-input`);
    if (!input) return;
    input.value = value || '';
    const clearBtn = document.getElementById(id)?.querySelector('.combobox-clear-btn');
    if (clearBtn) clearBtn.style.display = input.value.trim() ? 'inline-flex' : 'none';
  });
}

export function sanitizeUrlValue(url) {
  if (!url) return '#';

  const raw = String(url).trim();
  if (!raw) return '#';

  const hasExplicitScheme = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(raw);
  const isProtocolRelative = raw.startsWith('//');

  try {
    const baseOrigin = (typeof window !== 'undefined' && window.location?.origin)
      ? window.location.origin
      : 'https://sff.gg';
    const parsed = new URL(raw, baseOrigin);

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '#';

    // Relative URLs are accepted only as same-origin paths. Protocol-relative URLs
    // are treated as external absolute URLs and therefore must resolve to HTTP(S).
    if (!hasExplicitScheme && !isProtocolRelative) {
      if (parsed.origin !== baseOrigin) return '#';
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
    }

    return parsed.href;
  } catch (err) {
    debugRuntimeIssue('URL Sanitizer', err);
    return '#';
  }
}

export function sanitizeUrl(url) {
  return escapeHtml(sanitizeUrlValue(url));
}

export function debounce(fn, wait) { 
  let t; 
  return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; 
}
